/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import * as X from "@/ui";
import { buildMenu, useRoute, type MenuSection } from "./common";
import { LocaleSelectorMobile } from "./locale";

import ZapptaxDefaultLogo from "@/assets/shared-zapptax-default.svg";
import ZapptaxRetailLogo from "@/assets/shared-zapptax-retail.svg";
import ZapptaxShippingLogo from "@/assets/shared-zapptax-shipping.svg";

import { Header, useHeader } from "@/providers/strapi/content/types/singleTypes/header";
import ReactMarkdown from "react-markdown";



type Route = ReturnType<typeof useRoute>;

function getLogo(route: Route) {
  return (
    route[0] === "shipping" ? { image: ZapptaxShippingLogo, height: 44, alt: "Zapptax Shipping", to: "/shipping" as X.PageHref } :
      route[0] === "retail" ? { image: ZapptaxRetailLogo, height: 44, alt: "Zapptax Retail", to: "/retail" as X.PageHref } :
        { image: ZapptaxDefaultLogo, height: 24, alt: "Zapptax", to: "/" as X.PageHref }
  );
}


export function MenuMobile({
  isOpen,
  setIsOpenAction,
  withHeroImage,
  isScrolled,
  ...pageProps
}: X.PageProps<{
  isOpen: boolean;
  setIsOpenAction: (open: boolean) => void;
  withHeroImage?: boolean;
  isScrolled?: boolean;
  header?: Header
}>) {


  return (
    <>
      <MobileHeader
        {...pageProps}
        isScrolled={isScrolled}
        setMenuOpen={setIsOpenAction}
      />

      <MobileMenuPanel
        {...pageProps}
        isOpen={isOpen}
        setIsOpen={setIsOpenAction}
      />

      {/* {!withHeroImage && (
        <div className={X.clsx(
          "block xl:hidden h-header-mobile",
        )} />
      )} */}
    </>
  );
}


function MobileHeader({
  setMenuOpen,
  isScrolled,
  heroImage,
  //...pageProps
}: X.PageProps<{
  setMenuOpen: (open: boolean) => void;
  isScrolled?: boolean;
  heroImage?: boolean;
}>) {
  const route = useRoute('mobileHeader');
  const logo = getLogo(route);

  return (
    <header className={X.clsx(
      "xl:hidden flex justify-between items-center py-4 h-header-mobile pl-mobile-margin",
      isScrolled ? "bg-white/100" : heroImage ? "bg-white/0" : "bg-white/100",
      "transition-colors duration-300",
      "sticky top-0 right-0 left-0 z-30",
    )}>
      <X.Link href={logo.to} className="flex items-center">
        <X.Image
          src={logo.image}
          alt={logo.alt}
          height={logo.height}
        />
      </X.Link>
      <button onClick={() => setMenuOpen(true)} className="py-2 px-6">
        <X.Icon.Equal size={32} />
      </button>
    </header>
  );
}


function MobileMenuPanel({
  isOpen,
  setIsOpen,
  ...pageProps
}: X.PageProps<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}>) {
  // const tr = X.useTranslator(pageProps);
  const downloadUrl = X.useDownloadUrl(pageProps.locale);

  const route = useRoute('mobileHeader');
  const logo = getLogo(route);

  const closeMenu = () => setIsOpen(false);
  const [activeTab, setActiveTab] = X.useState<MenuSection>(
    route[0] === "retail" ? "retail" :
      route[0] === "aboutUs" ? "aboutUs" :
        "travelers"
  );

  const headerStrapi = useHeader() as Header
  const menu = buildMenu(headerStrapi);
  if (!menu) throw new Error('menu content not found')

  return (
    <div className={X.clsx(
      "block xl:hidden",
      "fixed inset-0 bg-white z-50 transform top-0 bottom-0 [-ms-overflow-style:none] [scrollbar-width:none]",
      "transition-transform duration-300 ease-in-out overflow-x-hidden w-dvw",
      isOpen ? "translate-x-0" : "-translate-x-full",
    )}>
      <div className="flex justify-between items-center py-4 pl-mobile-margin h-menu-mobile">
        <X.Link href="/" onClick={closeMenu} className="flex items-center">
          <X.Image
            src={logo.image}
            alt={logo.alt}
            height={logo.height}
            className="w-auto"
          />
        </X.Link>
        <button onClick={closeMenu} className="py-2 pr-6">
          <X.Icon.X size={28} />
        </button>
      </div>

      <div className="px-mobile-margin">
        <LocaleSelectorMobile
          {...pageProps}
          backgroundColor="white"
        />
      </div>

      <div className="mt-12 mb-8 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none]">
        <div className="flex space-x-2 px-mobile-margin">
          {Object.entries(menu).filter(([_, section]) => section.showMobile).map(([key, section]) => (
            <div key={key} onClick={() => setActiveTab(key as MenuSection)} className={X.clsx(
              "px-[9px] py-2 rounded-full text-[13px] border whitespace-nowrap",
              activeTab === key ? "bg-yellow border-yellow" : "border-black",
            )}>
              {/* {tr(section.label)} */}
              {/* {section.label} */}
              <ReactMarkdown>
                {section.label}
              </ReactMarkdown>
            </div>
          ))}
          <div>
            <div className="w-mobile-margin" />
          </div>
        </div>
      </div>

      <div className="px-mobile-margin">
        <div className={X.clsx("flex flex-col py-[4px] gap-2 overflow-x-hidden")}>
          {Object.entries(menu[activeTab]["sub"]).map(([key, sub]) => (
            sub.type === "button" ? (
              <div key={key} className="flex flex-row justify-start pt-2">
                <X.Button
                  secondary lg
                  // label={tr(sub.label) as string}
                  label={sub.label}
                  exitHref={sub.exitHref as string}
                  href={sub.href as never}
                />
              </div>
            ) : (
              <X.Link
                key={key}
                href={sub.href}
                onClick={closeMenu}
                className={X.clsx(
                  "flex flex-row gap-2 items-center py-[4px]",
                  sub.type === "large" ? "text-[20px] leading-tight pb-4" : "text-[14px] font-medium pl-4",
                )}
              >
                {/* {tr(sub.label)} */}
                <ReactMarkdown>
                  {sub.label}
                </ReactMarkdown>
                {sub.type === "large" ? <X.Icon.MoveRight size={20} /> : <X.Icon.ChevronRight size={16} />}
              </X.Link>
            )
          ))}
          <div className="flex flex-col items-start mb-4 mt-10 gap-4">
            <X.Button
              primary lg
              label={
                (menu[activeTab] && menu[activeTab].cta && menu[activeTab].cta[0]) ?
                  // tr(
                  //   {
                  //     en: menu[activeTab].cta[0]?.en || "Get the app",
                  //     fr: menu[activeTab].cta[0]?.fr || "Get the app",
                  //   }
                  // )
                  menu[activeTab].cta[0]
                  : "Get the app"
                // tr(
                //   { en: "Get the app", fr: "Téléchargez l'application" })
              }
              exitHref={downloadUrl}
            />
            {activeTab === "retail" && (
              <X.Button
                tertiary lg

                //exitHref={X.links.login}
                disabled={true}
                tooltip={
                  "Bientôt"
                  //   tr({
                  //   en: "Coming soon",
                  //   fr: "Bientôt",
                  // })
                }

                // label={tr({
                //   en: "Sign in",
                //   fr: "Se connecter",
                // })}
                label={
                  (menu[activeTab] && menu[activeTab].cta && menu[activeTab].cta[0]) ?
                    menu[activeTab].cta[1]
                    // tr(
                    //   {
                    //     en: menu[activeTab].cta[1]?.en || "Login",
                    //     fr: menu[activeTab].cta[1]?.fr || "Login",
                    //   }
                    // )
                    : "Get the app"
                  // tr(
                  //   { en: "Get the app", fr: "Téléchargez l'application" })
                }
              />
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
